import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import api from "../../config/URL";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "CENTER";

const StudentChangeClass = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    studentName: "",
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const canRead = Boolean(storedScreens?.changeClassRead);
  const canUpdate = Boolean(storedScreens?.changeClassUpdate);
  const defaultColumnVisibility = {
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "Status",
        enableHiding: true,
        size: 20,
        Cell: ({ row }) => {
          const isFinalStatus = ["CANCEL", "CLASS_ASSIGNED"].includes(
            row.original.status,
          );
          return isFinalStatus ? (
            <span
              className={`badge bg-${
                row.original.status === "CLASS_ASSIGNED" ? "success" : "danger"
              } fw-light`}
            >
              {row.original.status === "CLASS_ASSIGNED" ? "Assigned" : "Cancel"}
            </span>
          ) : (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setSelectedRow(row.original);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "centerName",
        header: "Centre Name",
        enableHiding: true,
      },
      {
        accessorKey: "studentUniqueId",
        header: "Student ID",
        enableHiding: true,
      },
      {
        accessorKey: "studentName",
        header: "Student Name",
        enableHiding: true,
      },
      {
        accessorKey: "currentCourse",
        header: "Course",
        enableHiding: true,
      },
      {
        accessorKey: "currentClass",
        header: "Class",
        enableHiding: true,
      },
      {
        accessorKey: "lastLessonDate",
        header: "Last Lesson Date",
        enableHiding: true,
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ],
    [],
  );

  /* -------------------- API -------------------- */
  const fetchApiData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("centerId", filters.centerId || "0");
      if (filters.studentName) {
        queryParams.append("studentName", filters.studentName);
      }

      const response = await api.get(
        `/getStudentChangeClass?${queryParams.toString()}`,
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);

  useEffect(() => {
    fetchApiData();
  }, [filters]);

  const handleCancel = async () => {
    if (!selectedRow) return;

    try {
      const response = await api.put(
        `/updateChangeClassStatus/${selectedRow.id}?changeClassStatus=CANCEL`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        setData((prev) =>
          prev.map((item) =>
            item.id === selectedRow.id ? { ...item, status: "CANCEL" } : item,
          ),
        );
        toast.success(
          "Student change class status updated to Cancel successfully.",
        );
        handleMenuClose();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred while cancelling.",
      );
    }
  };

  const handleAssign = () => {
    if (!selectedRow) return;
    navigate(
      `/changeClassAssigned?id=${selectedRow.id}&centerId=${selectedRow.centerId}&studentId=${selectedRow.studentId}`,
    );
    handleMenuClose();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      studentName: "",
    });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {},
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "StudentChangeClass.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: (e) => {
            if (e.target.closest(".MuiIconButton-root")) return;
            navigate(`/changeClass/view/${row.original.id}`);
          },
          style: { cursor: "pointer" },
        })
      : undefined,
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  return (
    <div className="container-fluid px-2 my-4 center">
      <ol className="breadcrumb my-3" style={{ listStyle: "none", margin: 0 }}>
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Student Management<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">Change Class List</li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Change Class List</strong>
          </span>
        </div>

        <div className="mb-3 d-flex">
          <input
            type="text"
            name="studentName"
            value={filters.studentName}
            onChange={handleFilterChange}
            className="form-control form-control-sm center_list ms-2"
            style={{ width: "160px" }}
            placeholder="Student Name"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={clearFilter}
            className="btn btn-sm btn-border ms-2"
          >
            Clear
          </button>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {canUpdate && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={handleAssign}
                  className="text-start mb-0 menuitem-style"
                >
                  Assign
                </MenuItem>
                <MenuItem
                  onClick={handleCancel}
                  className="text-start mb-0 menuitem-style"
                >
                  Cancel
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentChangeClass;
